import { EligibilityBenefits, EstimateInputs } from '@mmx/shared'

export type BenefitSlot = 'primary' | 'secondary'

export const BENEFIT_FIELDS = ['deductible', 'copayment', 'coinsurance', 'outOfPocketMax'] as const
export type BenefitField = typeof BENEFIT_FIELDS[number]

interface BenefitFieldAccessor {
  read: (inputs: EstimateInputs) => number | null
  write: (inputs: EstimateInputs, value: number | null) => EstimateInputs
  /** Untouched value on a never-synced estimate. Must match `EstimateBalanceFormService.defaultInputs()`. */
  untouched: number | null
}

// Explicit per-slot accessors rather than computed keys — `EstimateInputs` names the secondary
// fields differently, and indexing it by a built key loses type safety.
const ACCESSORS: Record<BenefitSlot, Record<BenefitField, BenefitFieldAccessor>> = {
  primary: {
    deductible: {
      read: i => i.deductible ?? null,
      write: (i, v) => ({ ...i, deductible: v ?? 0 }),
      untouched: 0,
    },
    copayment: {
      read: i => i.copayment ?? null,
      write: (i, v) => ({ ...i, copayment: v ?? 0 }),
      untouched: 0,
    },
    coinsurance: {
      read: i => i.coinsurance ?? null,
      write: (i, v) => ({ ...i, coinsurance: v ?? 0 }),
      untouched: 0,
    },
    outOfPocketMax: {
      read: i => i.outOfPocketMax ?? null,
      write: (i, v) => ({ ...i, outOfPocketMax: v }),
      untouched: null,
    },
  },
  secondary: {
    deductible: {
      read: i => i.secondaryDeductible ?? null,
      write: (i, v) => ({ ...i, secondaryDeductible: v ?? 0 }),
      untouched: 0,
    },
    copayment: {
      read: i => i.secondaryCopayment ?? null,
      write: (i, v) => ({ ...i, secondaryCopayment: v ?? 0 }),
      untouched: 0,
    },
    coinsurance: {
      read: i => i.secondaryCoinsurance ?? null,
      write: (i, v) => ({ ...i, secondaryCoinsurance: v ?? 0 }),
      untouched: 0,
    },
    outOfPocketMax: {
      read: i => i.secondaryOutOfPocketMax ?? null,
      write: (i, v) => ({ ...i, secondaryOutOfPocketMax: v }),
      untouched: null,
    },
  },
}

/** Tolerance, not `===`: coinsurance round-trips through percent conversion in the benefit cell. */
export function benefitValuesMatch(a: number | null | undefined, b: number | null | undefined): boolean {
  const left = a ?? null
  const right = b ?? null
  if (left === null || right === null) {
    return left === right
  }
  return Math.abs(left - right) < 1e-6
}

/** True when the payer returned conflicting values and eligibility couldn't settle on one. */
export function isBenefitAmbiguous(
  benefits: EligibilityBenefits | null | undefined,
  field: BenefitField,
): boolean {
  if (benefits == null) {
    return false
  }
  if (field === 'copayment') {
    return benefits.copaymentAmbiguous === true
  }
  if (field === 'coinsurance') {
    return benefits.coinsuranceAmbiguous === true
  }
  return false
}

export function readBenefitInput(
  inputs: EstimateInputs | null | undefined,
  slot: BenefitSlot,
  field: BenefitField,
): number | null {
  return inputs == null ? null : ACCESSORS[slot][field].read(inputs)
}

export interface BenefitsSyncResult {
  inputs: EstimateInputs
  /** New value for `estimate.originalBenefits` / `estimate.originalSecondaryBenefits`. */
  snapshot: EligibilityBenefits | null
  /** A value was adopted — the caller should markDirty so the next save persists it. */
  inputsChanged: boolean
  /** Inputs or the snapshot moved — the caller should push the estimate to state. */
  changed: boolean
}

/**
 * Folds a fetched eligibility response into `estimate.inputs`. A field still holding the `snapshot`
 * value was never touched by an operator and may be overwritten; anything else was typed by hand
 * and is preserved. The snapshot is then replaced wholesale.
 */
export function syncBenefitsIntoInputs(
  inputs: EstimateInputs,
  snapshot: EligibilityBenefits | null | undefined,
  benefits: EligibilityBenefits | null,
  slot: BenefitSlot,
): BenefitsSyncResult {
  if (benefits == null) {
    // Keep operator-typed values, but reset ones still holding the dropped snapshot's payer values.
    let cleared = inputs
    let clearedChanged = false
    if (snapshot != null) {
      for (const field of BENEFIT_FIELDS) {
        const accessor = ACCESSORS[slot][field]
        const current = accessor.read(inputs)
        if (benefitValuesMatch(current, snapshot[field]) && !benefitValuesMatch(current, accessor.untouched)) {
          cleared = accessor.write(cleared, accessor.untouched)
          clearedChanged = true
        }
      }
    }
    return {
      inputs: cleared,
      snapshot: null,
      inputsChanged: clearedChanged,
      changed: (snapshot ?? null) !== null,
    }
  }

  let next = inputs
  let inputsChanged = false

  for (const field of BENEFIT_FIELDS) {
    const incoming = benefits[field] ?? null
    if (incoming === null || isBenefitAmbiguous(benefits, field)) {
      // Nothing usable to adopt: absent from the response, or ambiguous.
      continue
    }

    const accessor = ACCESSORS[slot][field]
    const current = accessor.read(inputs)
    // On a never-synced estimate the baseline is the untouched default. That preserves a value an
    // operator typed before this feature existed, at the cost of one case we can't recover: a
    // deliberate 0 on such an estimate is indistinguishable from untouched and will be adopted over.
    const baseline = snapshot != null ? (snapshot[field] ?? null) : accessor.untouched

    if (!benefitValuesMatch(current, baseline) || benefitValuesMatch(current, incoming)) {
      continue
    }

    next = accessor.write(next, incoming)
    inputsChanged = true
  }

  return { inputs: next, snapshot: benefits, inputsChanged, changed: true }
}
